// frontend/src/components/DocumentProcessor/StepBar.tsx
import React from 'react';
import { Box } from '@mui/material';
import { brand } from '../../theme';
import { stepperIcons } from '../../assets/icons/nav';
import NavIcon from '../../shared/components/NavIcon';

export type WorkflowStep = 'upload' | 'analyse' | 'review';

const STEP_ORDER: WorkflowStep[] = ['upload', 'analyse', 'review'];

const STEP_ICONS: Record<WorkflowStep, string> = {
  upload: stepperIcons.uploadDocument,
  analyse: stepperIcons.analyse,
  review: stepperIcons.review,
};

interface StepBarProps {
  step: WorkflowStep;
  labels: Record<WorkflowStep, string>;
  isStepAccessible?: (target: WorkflowStep) => boolean;
  onStepClick?: (target: WorkflowStep) => void;
}

// Column / circle / track geometry. The connector lives in the same
// flex container as the step columns so its endpoints lock to the
// outermost circle centers — not the parent's full width.
const STEP_COLUMN_WIDTH = 260;       // fixed width per step column (wider spacing)
const CIRCLE_SIZE = 18;              // circle outer diameter (border-box)
const INNER_DOT_SIZE = 8;            // inner filled dot for radio-style states
const TRACK_HEIGHT = 3;              // connector line thickness

// Vertical offset of the track's top edge so it crosses the circle center.
// Measured against the INNER positioning Box (which has no padding of its own);
// the outer wrapper's py: 3 is irrelevant here. = (CIRCLE_SIZE - TRACK_HEIGHT) / 2.
const TRACK_TOP = CIRCLE_SIZE / 2 - TRACK_HEIGHT / 2;

const StepBar: React.FC<StepBarProps> = ({ step, labels, isStepAccessible, onStepClick }) => {
  const activeIndex = STEP_ORDER.indexOf(step);
  const lastIndex = STEP_ORDER.length - 1;
  const progressFraction = lastIndex === 0 ? 0 : activeIndex / lastIndex;

  // The inner container holds the step columns AND the absolute tracks.
  // Its width is the sum of column widths, so the tracks (inset by half
  // a column on each side) span exactly from the first circle's center
  // to the last circle's center.
  const trackInset = STEP_COLUMN_WIDTH / 2;

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        py: 3,
        px: 4,
      }}
    >
      <Box
        sx={{
          position: 'relative',
          display: 'flex',
          alignItems: 'flex-start',
        }}
      >
        {/* Background track — from first dot's right edge to last dot's left edge */}
        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            top: TRACK_TOP,
            left: `${trackInset + CIRCLE_SIZE / 2}px`,
            right: `${trackInset + CIRCLE_SIZE / 2}px`,
            height: TRACK_HEIGHT,
            backgroundColor: '#D5D5D5',
            zIndex: 0,
          }}
        />
        {/* Progress track — from first dot's right edge to the active dot's left edge */}
        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            top: TRACK_TOP,
            left: `${trackInset + CIRCLE_SIZE / 2}px`,
            width: activeIndex === 0
              ? 0
              : `calc((100% - ${trackInset * 2}px) * ${progressFraction} - ${CIRCLE_SIZE}px)`,
            height: TRACK_HEIGHT,
            backgroundColor: brand.orange,
            zIndex: 0,
            transition: 'width 240ms ease',
          }}
        />

        {STEP_ORDER.map((target) => {
          const i = STEP_ORDER.indexOf(target);
          const isActive = i === activeIndex;
          const isComplete = i < activeIndex;
          const accessible = isStepAccessible ? isStepAccessible(target) : true;
          const clickable = !!onStepClick && accessible;
          const filled = isActive || isComplete;

          return (
            <Box
              key={target}
              onClick={() => { if (clickable) onStepClick!(target); }}
              sx={{
                position: 'relative',
                zIndex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 1,
                cursor: clickable ? 'pointer' : 'default',
                width: STEP_COLUMN_WIDTH,
                flexShrink: 0,
              }}
            >
              <Box
                sx={{
                  width: CIRCLE_SIZE,
                  height: CIRCLE_SIZE,
                  borderRadius: '50%',
                  backgroundColor: '#fff',
                  border: `2px solid ${filled ? brand.orange : '#D5D5D5'}`,
                  boxSizing: 'border-box',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {/* Inner dot — radio-button style for all steps; color reflects state */}
                <Box
                  sx={{
                    width: INNER_DOT_SIZE,
                    height: INNER_DOT_SIZE,
                    borderRadius: '50%',
                    backgroundColor: filled ? brand.orange : '#D5D5D5',
                  }}
                />
              </Box>
              <NavIcon src={STEP_ICONS[target]} size={28} sx={{ opacity: isActive ? 1 : 0.5 }} />
              <Box
                sx={{
                  fontSize: 14,
                  fontWeight: isActive ? 600 : 500,
                  color: isActive ? brand.textPrimary : brand.textMuted,
                }}
              >
                {labels[target]}
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default StepBar;
